import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { createFolder, addShortcut, updateShortcut } from '../services/desktopService';
import Modal from './Modal';
import './ShortcutCreator.css';

function ShortcutCreator({ userId, currentFolder, position, initialType, editingShortcut, onClose }) {
  const [shortcutType, setShortcutType] = useState(
    editingShortcut ? editingShortcut.type : (initialType || 'app')
  );
  const [selectedApp, setSelectedApp] = useState(editingShortcut?.appId || '');
  const [selectedNote, setSelectedNote] = useState(editingShortcut?.noteId || '');
  const [urlTitle, setUrlTitle] = useState(editingShortcut?.title || '');
  const [urlAddress, setUrlAddress] = useState(editingShortcut?.url || '');
  const [folderName, setFolderName] = useState('');
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);

  // Use provided position or default to top-left
  const shortcutPosition = position || editingShortcut?.position || { x: 50, y: 50 };

  const apps = [
    { id: 'defects', title: 'Defect Tracker', icon: '🐛' },
    { id: 'features', title: 'Feature Requests', icon: '✨' },
    { id: 'notes', title: 'Notes', icon: '📝' },
    { id: 'messaging', title: 'Messaging', icon: '💬' },
    { id: 'diagramming', title: 'Diagramming', icon: '📐' }
  ];

  // Load user's notes
  useEffect(() => {
    const loadNotes = async () => {
      const notesQuery = query(collection(db, 'notes'));
      const snap = await getDocs(notesQuery);
      const allNotes = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const userNotes = allNotes.filter(note => 
        note.createdBy === userId || 
        note.sharedWith?.some(s => s.userId === userId)
      );
      setNotes(userNotes);
    };
    
    if (shortcutType === 'note') {
      loadNotes();
    }
  }, [shortcutType, userId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let shortcut;

      if (shortcutType === 'app') {
        const app = apps.find(a => a.id === selectedApp);
        if (!app) return;

        shortcut = {
          id: `shortcut-${Date.now()}`,
          type: 'app',
          appId: app.id,
          title: app.title,
          icon: app.icon,
          position: shortcutPosition,
          folderId: currentFolder || null
        };
      } else if (shortcutType === 'note') {
        const note = notes.find(n => n.id === selectedNote);
        if (!note) return;

        shortcut = {
          id: `shortcut-${Date.now()}`,
          type: 'note',
          noteId: note.id,
          title: note.title,
          icon: '📄',
          position: shortcutPosition,
          folderId: currentFolder || null
        };
      } else if (shortcutType === 'url') {
        if (!urlTitle || !urlAddress) return;

        shortcut = {
          id: `shortcut-${Date.now()}`,
          type: 'url',
          url: urlAddress,
          title: urlTitle,
          icon: '🌐',
          position: shortcutPosition,
          folderId: currentFolder || null
        };
      } else if (shortcutType === 'folder') {
        if (!folderName) return;
        
        await createFolder(userId, folderName, shortcutPosition);
        onClose();
        setLoading(false);
        return;
      }

      if (editingShortcut) {
        // Update existing shortcut
        const updates = {};
        if (shortcutType === 'app') {
          const app = apps.find(a => a.id === selectedApp);
          if (app) {
            updates.appId = app.id;
            updates.title = app.title;
            updates.icon = app.icon;
          }
        } else if (shortcutType === 'note') {
          const note = notes.find(n => n.id === selectedNote);
          if (note) {
            updates.noteId = note.id;
            updates.title = note.title;
          }
        } else if (shortcutType === 'url') {
          updates.url = urlAddress;
          updates.title = urlTitle;
        }
        
        await updateShortcut(userId, editingShortcut.id, updates);
      } else {
        // Create new shortcut
        await addShortcut(userId, shortcut);
      }
      
      onClose();
    } catch (err) {
      console.error('Error saving shortcut:', err);
      alert('Error saving shortcut');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title={editingShortcut ? "Edit Shortcut" : "Create Desktop Shortcut"}>
      <form onSubmit={handleSubmit} style={{maxHeight: '500px', overflowY: 'auto'}}>
        <div className="form-group">
          <label>Shortcut Type</label>
          <select
            value={shortcutType}
            onChange={e => setShortcutType(e.target.value)}
            disabled={!!editingShortcut}
            required
          >
            <option value="app">Application</option>
            <option value="note">Note</option>
            <option value="url">URL</option>
            <option value="folder">Folder</option>
          </select>
          {editingShortcut && <p style={{fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem'}}>Type cannot be changed when editing</p>}
        </div>

        {shortcutType === 'app' && (
          <div className="form-group">
            <label>Select Application</label>
            <select
              value={selectedApp}
              onChange={e => setSelectedApp(e.target.value)}
              required
              size="5"
            >
              <option value="">Choose an app...</option>
              {apps.map(app => (
                <option key={app.id} value={app.id}>
                  {app.icon} {app.title}
                </option>
              ))}
            </select>
          </div>
        )}

        {shortcutType === 'note' && (
          <div className="form-group">
            <label>Select Note</label>
            <select
              value={selectedNote}
              onChange={e => setSelectedNote(e.target.value)}
              required
            >
              <option value="">Choose a note...</option>
              {notes.map(note => (
                <option key={note.id} value={note.id}>
                  {note.title}
                </option>
              ))}
            </select>
          </div>
        )}

        {shortcutType === 'url' && (
          <>
            <div className="form-group">
              <label>Shortcut Title</label>
              <input
                type="text"
                value={urlTitle}
                onChange={e => setUrlTitle(e.target.value)}
                placeholder="e.g., Google, GitHub"
                required
              />
            </div>
            <div className="form-group">
              <label>URL Address</label>
              <input
                type="url"
                value={urlAddress}
                onChange={e => setUrlAddress(e.target.value)}
                placeholder="https://example.com"
                required
              />
            </div>
          </>
        )}

        {shortcutType === 'folder' && (
          <div className="form-group">
            <label>Folder Name</label>
            <input
              type="text"
              value={folderName}
              onChange={e => setFolderName(e.target.value)}
              placeholder="e.g., Work Files, Projects"
              required
            />
          </div>
        )}

        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Creating...' : 'Create Shortcut'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default ShortcutCreator;
